import React, { useEffect, useState, useCallback } from "react";
import BaseLayout from "./BaseLayout";
import "../App.css";
import axios from 'axios';
import { API_BASE } from '../api';
import { fetchAllPages } from '../apiUtils';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { authData, updateUserAttribute } = useAuth();
  const minimumGamesThreshold = 40; 
  const winBonusMultiplier = 0.3;
  const lossBonusMultiplier = 0.2;
  const gameMultiplier = 0.1;

  const calculatePercentage = (wins, losses) => {
    if (wins === 0 && losses === 0) {
      const value = 0;
      return value.toFixed(1);
    }
    return ((wins / (wins + losses)) * 100).toFixed(1);
  };

  const sortUsers = useCallback((users) => {
    return users.sort((a, b) => {
      const totalGamesA = a.score + a.losses;
      const totalGamesB = b.score + b.losses;
      let winPercentageA = calculatePercentage(a.score, a.losses);
      if (winPercentageA === 100.0) {
        winPercentageA = 200.0;
      }
      let winPercentageB = calculatePercentage(b.score, b.losses);
      if (winPercentageB === 100.0) {
        winPercentageB = 200.0;
      }
  
      const gameModifierA = Math.min(totalGamesA / minimumGamesThreshold, 1);
      const gameModifierB = Math.min(totalGamesB / minimumGamesThreshold, 1);
  
      const adjustedScoreA = (winPercentageA * gameModifierA) + (a.score * winBonusMultiplier) - (a.losses * lossBonusMultiplier) + ((totalGamesA / 5) * gameMultiplier);
      const adjustedScoreB = (winPercentageB * gameModifierB) + (b.score * winBonusMultiplier) - (b.losses * lossBonusMultiplier) + ((totalGamesB / 5) * gameMultiplier);
  
      return adjustedScoreB - adjustedScoreA || totalGamesB - totalGamesA;
    });
  }, [minimumGamesThreshold, winBonusMultiplier, lossBonusMultiplier, gameMultiplier]);

  const updateRanks = useCallback(async (users) => {
    for (let i = 0; i < users.length; i++) {
      let newRank = 0;
      if (i < 3) {
        newRank = i + 1;
      }
  
      if (users[i].rank !== newRank) {
        try {
          await axios.put(`${API_BASE}/data/update-user/${users[i].id}`, {
            rank: newRank
          });
          if (authData.user?.id === users[i].id) {
            updateUserAttribute('rank', newRank);
          }
        } catch (error) {
          console.error(`Failed to update rank for ${users[i].firstName} ${users[i].lastName}`, error);
        }
      }
    }
  }, [authData.user?.id, updateUserAttribute]);

  useEffect(() => {
    document.body.classList.add("account-page-bg");
    const fetchLeaderboardData = async () => {
      try {
        const users = await fetchAllPages(`${API_BASE}/data/get-users`, 'users');
        const sortedData = sortUsers(users);
        setLeaderboardData(sortedData);
        updateRanks(sortedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setLoading(false);
      }
    };

    fetchLeaderboardData();
    return () => {
      document.body.classList.remove("account-page-bg");
    };
  }, [sortUsers, updateRanks]);

  const handleUserClick = (username) => {
    navigate(`/user/${username}`);
  };

  return (
    <BaseLayout>
      <div className="leaderboard-container">
        <h3 style={{ fontSize: "32px" }}>Leaderboard</h3>
        <div className="leaderboard-content">
          {loading ? (
            <p>Loading leaderboard...</p>
          ) : (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>User</th>
                  <th>Overall Record</th>
                  <th>Winning Percentage</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.map((user, index) => (
                  <tr key={user.id}>
                    <td>#{index + 1}</td>
                    <td onClick={() => handleUserClick(user.username)} style={{ cursor: 'pointer' }}>
                      {`${user.firstName} ${user.lastName}`}
                    </td>
                    <td>{user.score} - {user.losses}</td>
                    <td>{calculatePercentage(user.score, user.losses)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default Leaderboard;